import AttachmentButton, {
    AttachmentAction,
} from "@/components/mailEditor/AttachmentButton";
import RichTextToolbar from "@/components/mailEditor/RichTextToolbar";
import { useEditorContext } from "@/contexts/EditorContext";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import { Tone } from "@/hooks/useGenerateAnswerWithIA";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import Spinner from "../ui/spinner";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";
import ConfirmSendMailDialog from "./ConfirmSendMailDialog";
import IAToolbar, { IAToolbarVariant } from "./IAToolbar";

interface SendWarning {
    id: string;
    message: string;
}

interface MailInputFooterProps {
    isExpanded: boolean;
    isFullScreen: boolean;
    isSending: boolean;
    onSendMail: () => void;
    isSendButtonDisabled?: boolean;
    sendWarnings?: SendWarning;
    attachments?: EmailAttachment[];
    onAttachmentsChange?: (
        attachments: EmailAttachment[],
        action: AttachmentAction,
    ) => void;
    makeSuggestion: (action: Tone) => void;
    acceptOrRefuseSuggestion: (accept: boolean) => void;
    isLoading: boolean;
    isPendingChange: boolean;
}

const MailInputFooter = ({
    isExpanded,
    isFullScreen,
    isSending,
    onSendMail,
    isSendButtonDisabled = false,
    sendWarnings = undefined,
    attachments,
    onAttachmentsChange,
    makeSuggestion,
    acceptOrRefuseSuggestion,
    isLoading,
    isPendingChange,
}: MailInputFooterProps) => {
    const { t } = useTranslation();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const { editor } = useEditorContext();

    const handleSendClick = () => {
        if (sendWarnings) {
            setShowConfirmDialog(true);
        } else {
            onSendMail();
        }
    };

    const handleConfirmSend = () => {
        setShowConfirmDialog(false);
        onSendMail();
    };

    const sendButton = (
        <Button
            disabled={isSendButtonDisabled}
            onClick={handleSendClick}
            size="lg"
            variant="purpleGradientWithoutBorder"
        >
            {t("common.send")}
        </Button>
    );

    const renderSendButton = () => {
        if (isSending) {
            return <Spinner />;
        }

        if (isSendButtonDisabled) {
            return (
                <TooltipProvider delayDuration={100}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div>{sendButton}</div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t("email.sendButtonDisabled")}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            );
        }

        return sendButton;
    };

    return (
        <>
            <div
                className={cn(
                    "flex h-0 w-full items-center justify-between overflow-hidden transition-all duration-300",
                    isExpanded && "min-h-12",
                    isFullScreen && "h-auto items-end",
                )}
            >
                {/* Shrinks on a narrow screen (the assistant's side panel, a phone): the toolbar then
                    scrolls sideways and « Envoyer » keeps its place. */}
                <div className="flex min-w-0 flex-col items-start gap-1">
                    <IAToolbar
                        acceptOrRefuseSuggestion={acceptOrRefuseSuggestion}
                        isLoading={isLoading}
                        isPendingChange={isPendingChange}
                        makeSuggestion={makeSuggestion}
                        variant={
                            isFullScreen
                                ? IAToolbarVariant.FULL_SCREEN
                                : IAToolbarVariant.DEFAULT
                        }
                    />
                    {isFullScreen && (
                        <AttachmentButton
                            attachments={attachments}
                            onAttachmentsChange={onAttachmentsChange}
                        />
                    )}
                    {isFullScreen && editor && (
                        <RichTextToolbar
                            className="max-w-full flex-nowrap justify-start overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                            editor={editor}
                        />
                    )}
                </div>

                <div className="flex flex-none items-center gap-2">
                    {renderSendButton()}
                </div>
            </div>

            <ConfirmSendMailDialog
                description={sendWarnings?.message || ""}
                isOpen={showConfirmDialog}
                onConfirm={handleConfirmSend}
                onOpenChange={setShowConfirmDialog}
                title={t("email.warnings.confirmSend")}
            />
        </>
    );
};

export default MailInputFooter;
